import * as React from "react";
import { ThemeInstallConfigurationResource, themeInstallConfigurationGraphQlToResource } from "@repo/theme-bridge";
import { lazy, useEffect, useState } from "react";
import FullScreenSpinner from "../../ApplicationIndex/components/FullScreenSpinner";
import { Params, useParams } from "react-router-dom";
import qs, { ParsedQs } from "qs";
import { Currency } from "dinero.js";
import type {
    LoadMarketplaceThemeOptions,
    LoadMarketplaceThemeOptionsBundle,
    LoadMarketplaceThemeOptionsTransaction,
} from "./provider";
import {
    getBundleThemeConfiguration,
    getActivation,
    getIntegration,
    getOverrideThemeConfiguration,
    getTransactionThemeConfiguration,
    getIntegrationProducts,
    CollectionProduct,
    setApplicationKey,
} from "@repo/core";
import { ErrorPage } from "../../ApplicationIndex/MiscPages";
const MarketplaceThemeProvider = lazy(() => import("./provider"));

export const newTransactionWorkflowOptionsBuilder = (
    _routeParams: Readonly<Params<string>>,
    queryParams: ParsedQs,
): LoadMarketplaceThemeOptionsTransaction => {
    const integrationProductId = queryParams["integrationProductId"] as string | undefined;
    const externalIntegrationId = queryParams["externalIntegrationId"] as string | undefined;
    const externalProductId = queryParams["externalProductId"] as string | undefined;
    const workflowId = queryParams["workflowId"] as string | undefined;
    const designName = queryParams["name"] as string | undefined;
    if (integrationProductId) {
        return {
            options: {
                integrationProductId,
                workflowId,
                designName,
                type: "integration",
            },
            type: "transaction",
        };
    }
    if (externalIntegrationId && externalProductId) {
        return {
            options: {
                externalIntegrationId,
                externalProductId,
                workflowId,
                designName,
                type: "external",
            },
            type: "transaction",
        };
    }
    throw new Error("Invalid launch parameters.");
};

export const continueTransactionWorkflowOptionsBuilder = (
    routeParams: Readonly<Params<string>>,
): LoadMarketplaceThemeOptionsTransaction => {
    return {
        options: {
            transactionId: routeParams.transactionId!,
            type: "transaction",
        },
        type: "transaction",
    };
};

export const newBundleOptionsBuilder = (
    _routeParams: Readonly<Params<string>>,
    queryParams: ParsedQs,
): LoadMarketplaceThemeOptionsBundle => {
    return {
        options: {
            productCollectionId: queryParams["productCollectionId"] as string | undefined,
            integrationProductId: queryParams["integrationProductId"] as string | undefined,
            type: "new",
        },
        type: "bundle",
    };
};

export const existingBundleOptionsBuilder = (
    routeParams: Readonly<Params<string>>,
): LoadMarketplaceThemeOptionsBundle => {
    return {
        options: {
            bundleId: routeParams.bundleId!,
            type: "existing",
        },
        type: "bundle",
    };
};

export enum ConfigurationError {
    NO_APPLICATION_KEY = "NO_APPLICATION_KEY",
    NO_THEME = "NO_THEME",
    INVALID_LAUNCH_PARAMS = "INVALID_LAUNCH_PARAMS",
    OVERRIDE_THEME_FAILED = "OVERRIDE_THEME_FAILED",
    ACTIVATION_THEME_FAILED = "ACTIVATION_THEME_FAILED",
    TRANSACTION_THEME_FAILED = "TRANSACTION_THEME_FAILED",
    BUNDLE_THEME_FAILED = "BUNDLE_THEME_FAILED",
    INTEGRATION_FETCH_FAILED = "INTEGRATION_FETCH_FAILED",
    NETWORK_ERROR = "NETWORK_ERROR",
    SINGLE_PRODUCT_MISSING_PRODUCT = "SINGLE_PRODUCT_MISSING_PRODUCT",
    SINGLE_PRODUCT_NOT_IN_COLLECTION = "SINGLE_PRODUCT_NOT_IN_COLLECTION",
}

interface ErrorDetails {
    error: ConfigurationError;
    message?: string;
    context?: Record<string, unknown>;
}

export const withMarketplaceTheme = (
    getOptions: (routeParams: Readonly<Params<string>>, queryParams: ParsedQs) => LoadMarketplaceThemeOptions,
    Fallback?: React.FunctionComponent,
) => {
    // eslint-disable-next-line react/display-name
    return () => {
        const params = useParams();
        const queryStringParams = qs.parse(window.location.hash.split("?")[1], { ignoreQueryPrefix: true });
        const [activationId] = useState(queryStringParams["activationId"] as string | undefined);
        const [applicationKey] = useState(queryStringParams["applicationKey"] as string | undefined);
        const [presentmentCurrency] = useState(queryStringParams["currencyCode"] as Currency | undefined);

        // DEPRECATED
        const [overrideThemeInstallId] = useState(queryStringParams["overrideThemeInstallId"] as string | undefined);
        const [overrideThemeConfigurationId] = useState(
            queryStringParams["overrideThemeConfigurationId"] as string | undefined,
        );
        // END DEPRECATED

        const [configuration, setConfiguration] = useState<ThemeInstallConfigurationResource | undefined>(undefined);
        const [loading, setLoading] = useState(true);
        const [partnerConfigError, setPartnerConfigError] = useState<ConfigurationError | undefined>(undefined);
        const [errorDetails, setErrorDetails] = useState<ErrorDetails | undefined>(undefined);
        const [options, setOptions] = useState<LoadMarketplaceThemeOptions | undefined>();

        useEffect(() => {
            if (applicationKey) {
                setApplicationKey(applicationKey);
            }
            const fetch = async () => {
                try {
                    // assign getOptions to ref inside fetch so if an exception throws we can handle the error
                    const options = getOptions(params, queryStringParams);

                    // Not able to resolve currentIntegration without an application key
                    if (!applicationKey) {
                        return {
                            error: ConfigurationError.NO_APPLICATION_KEY,
                            message: "No application key provided in URL parameters",
                            context: { queryParams: Object.keys(queryStringParams) },
                        };
                    }

                    // If an overriding theme has been specified, pull this first.
                    // DEPRECATED
                    if (overrideThemeInstallId && overrideThemeConfigurationId) {
                        console.log(
                            "[Theme Resolution] Attempting override theme:",
                            overrideThemeConfigurationId,
                            overrideThemeInstallId,
                        );
                        try {
                            const theme = await getOverrideThemeConfiguration(
                                overrideThemeConfigurationId,
                                overrideThemeInstallId,
                            );
                            if (theme) {
                                console.log("[Theme Resolution] ✓ Override theme found");
                                setOptions(options);
                                return { data: theme };
                            }
                            console.warn("[Theme Resolution] ✗ Override theme returned no data");
                        } catch (err) {
                            console.error("[Theme Resolution] Override theme fetch failed:", err);
                            return {
                                error: ConfigurationError.OVERRIDE_THEME_FAILED,
                                message: err instanceof Error ? err.message : "Unknown error",
                                context: { overrideThemeConfigurationId, overrideThemeInstallId },
                            };
                        }
                    }

                    // Try to resolve the theme from the activation
                    if (activationId) {
                        console.log("[Theme Resolution] Attempting activation theme:", activationId);
                        try {
                            const integrationProductId =
                                options.type === "bundle" && options.options.type === "new"
                                    ? options.options.integrationProductId
                                    : undefined;
                            let integrationProduct: { id: string; product?: { id: string } } | undefined = undefined;
                            let productIds: string[] | undefined = undefined;
                            if (integrationProductId) {
                                const integrationProducts = await getIntegrationProducts([integrationProductId]);
                                integrationProduct = integrationProducts[0];
                                if (integrationProduct.product?.id) {
                                    productIds = [integrationProduct.product.id];
                                }
                            }

                            const activation = await getActivation(activationId, productIds);
                            if (activation?.marketplaceThemeInstallConfiguration) {
                                console.log("[Theme Resolution] ✓ Activation theme found");

                                // Single-product mode validation
                                if (activation.singleProductMode === true) {
                                    if (!integrationProduct) {
                                        return {
                                            error: ConfigurationError.SINGLE_PRODUCT_MISSING_PRODUCT,
                                            message:
                                                "This activation requires a product. Please launch from a product page.",
                                            context: { activationId },
                                        };
                                    }

                                    const matchedProduct = activation.activationProductCollections
                                        ?.flatMap((apc) => apc.productCollection.productCollectionProducts)
                                        .find((p) => p?.productId === integrationProduct.product!.id);

                                    if (!matchedProduct) {
                                        return {
                                            error: ConfigurationError.SINGLE_PRODUCT_NOT_IN_COLLECTION,
                                            message: "This product is not configured for this activation.",
                                            context: { activationId, integrationProductId },
                                        };
                                    }

                                    const productCollectionId =
                                        activation.activationProductCollections?.[0]?.productCollection.id;

                                    if (options.type === "bundle" && options.options.type === "new") {
                                        const enrichedOptions: LoadMarketplaceThemeOptionsBundle = {
                                            type: "bundle",
                                            options: {
                                                ...options.options,
                                                singleProductMode: true,
                                                collectionProduct: new CollectionProduct(matchedProduct),
                                                productCollectionId:
                                                    productCollectionId || options.options.productCollectionId,
                                            },
                                        };
                                        setOptions(enrichedOptions);
                                        return { data: activation.marketplaceThemeInstallConfiguration };
                                    }
                                }

                                setOptions(options);
                                return { data: activation.marketplaceThemeInstallConfiguration };
                            }
                            console.warn("[Theme Resolution] ✗ Activation has no theme configured");
                        } catch (err) {
                            console.error("[Theme Resolution] Activation theme fetch failed:", err);
                            return {
                                error: ConfigurationError.ACTIVATION_THEME_FAILED,
                                message: err instanceof Error ? err.message : "Unknown error",
                                context: { activationId },
                            };
                        }
                    }

                    // Try to resolve the theme from the transaction
                    // DEPRECATED
                    if (options.type === "transaction" && options.options.type === "transaction") {
                        const transactionId = options.options.transactionId;
                        console.log("[Theme Resolution] Attempting transaction theme:", transactionId);
                        try {
                            const theme = await getTransactionThemeConfiguration(transactionId);
                            if (theme) {
                                console.log("[Theme Resolution] ✓ Transaction theme found");
                                setOptions(options);
                                return { data: theme };
                            }
                            console.warn("[Theme Resolution] ✗ Transaction has no theme configured");
                        } catch (err) {
                            console.error("[Theme Resolution] Transaction theme fetch failed:", err);
                            return {
                                error: ConfigurationError.TRANSACTION_THEME_FAILED,
                                message: err instanceof Error ? err.message : "Unknown error",
                                context: { transactionId },
                            };
                        }
                    }

                    // Try to resolve the theme from the bundle
                    if (options.type === "bundle" && options.options.type === "existing") {
                        const bundleId = options.options.bundleId;
                        console.log("[Theme Resolution] Attempting bundle theme:", bundleId);
                        try {
                            const theme = await getBundleThemeConfiguration(bundleId);
                            if (theme) {
                                console.log("[Theme Resolution] ✓ Bundle theme found");
                                setOptions(options);
                                return { data: theme };
                            }
                            console.warn("[Theme Resolution] ✗ Bundle has no theme configured");
                        } catch (err) {
                            console.error("[Theme Resolution] Bundle theme fetch failed:", err);
                            return {
                                error: ConfigurationError.BUNDLE_THEME_FAILED,
                                message: err instanceof Error ? err.message : "Unknown error",
                                context: { bundleId },
                            };
                        }
                    }

                    // Pull information about the integration
                    // This is a fallback case providing a global theme to use as default for the integration if no other theme is found.
                    try {
                        const currentIntegration = await getIntegration(applicationKey, overrideThemeConfigurationId);
                        if (currentIntegration?.marketplaceThemeInstallConfiguration) {
                            console.log("[Theme Resolution] ✓ Integration default theme found");
                            setOptions(options);
                            return { data: currentIntegration.marketplaceThemeInstallConfiguration };
                        }
                        console.error(
                            "[Theme Resolution] ✗ Integration has no default theme configured",
                            currentIntegration,
                        );
                        return {
                            error: ConfigurationError.NO_THEME,
                            message: "Integration exists but has no default theme configured",
                            context: {
                                integrationFound: !!currentIntegration,
                                hasTheme: !!currentIntegration?.marketplaceThemeInstallConfiguration,
                            },
                        };
                    } catch (err) {
                        console.error("[Theme Resolution] Integration fetch failed:", err);
                        return {
                            error: ConfigurationError.INTEGRATION_FETCH_FAILED,
                            message: err instanceof Error ? err.message : "Unknown error",
                            context: {},
                        };
                    }
                } catch (err) {
                    console.error("[Theme Resolution] Unexpected error in theme resolution:", err);
                    return {
                        error: ConfigurationError.INVALID_LAUNCH_PARAMS,
                        message: err instanceof Error ? err.message : "Unexpected error during theme resolution",
                        context: { stack: err instanceof Error ? err.stack : undefined },
                    };
                }
            };

            fetch()
                .then((value) => {
                    if ("error" in value && value.error) {
                        console.error("[Theme Resolution] Final error:", value);
                        setPartnerConfigError(value.error);
                        setErrorDetails({
                            error: value.error,
                            message: value.message,
                            context: value.context,
                        });
                    }
                    if ("data" in value && value.data) {
                        console.log("[Theme Resolution] Theme successfully loaded");
                        setConfiguration(themeInstallConfigurationGraphQlToResource(value.data));
                    }
                })
                .catch((err) => {
                    console.error("[Theme Resolution] Promise rejection:", err);
                    const errorDetail: ErrorDetails = {
                        error: ConfigurationError.NETWORK_ERROR,
                        message: err instanceof Error ? err.message : "Network or unexpected error",
                        context: { type: typeof err, stack: err instanceof Error ? err.stack : undefined },
                    };
                    setPartnerConfigError(errorDetail.error);
                    setErrorDetails(errorDetail);
                })
                .finally(() => setLoading(false));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [applicationKey, overrideThemeConfigurationId, overrideThemeInstallId]);

        if (partnerConfigError) {
            if (Fallback) return <Fallback />;
            const getTitle = () => {
                switch (partnerConfigError) {
                    case ConfigurationError.NO_APPLICATION_KEY:
                        return "Application Key Missing";
                    case ConfigurationError.NO_THEME:
                        return "Theme Configuration Issue";
                    case ConfigurationError.INVALID_LAUNCH_PARAMS:
                        return "Invalid Launch Parameters";
                    case ConfigurationError.OVERRIDE_THEME_FAILED:
                        return "Override Theme Load Failed";
                    case ConfigurationError.ACTIVATION_THEME_FAILED:
                        return "Activation Theme Load Failed";
                    case ConfigurationError.TRANSACTION_THEME_FAILED:
                        return "Transaction Theme Load Failed";
                    case ConfigurationError.BUNDLE_THEME_FAILED:
                        return "Bundle Theme Load Failed";
                    case ConfigurationError.INTEGRATION_FETCH_FAILED:
                        return "Integration Configuration Failed";
                    case ConfigurationError.NETWORK_ERROR:
                        return "Network Error";
                    case ConfigurationError.SINGLE_PRODUCT_MISSING_PRODUCT:
                        return "Product Required";
                    case ConfigurationError.SINGLE_PRODUCT_NOT_IN_COLLECTION:
                        return "Product Not Configured";
                    default:
                        return "Configuration Error";
                }
            };
            const getDescription = () => {
                switch (partnerConfigError) {
                    case ConfigurationError.NO_APPLICATION_KEY:
                        return "The application key is missing from the URL. Please contact the store owner to ensure the integration is properly configured.";
                    case ConfigurationError.NO_THEME:
                        return "No theme is configured for this product experience. The store owner needs to configure a theme in their Spiff Commerce settings.";
                    case ConfigurationError.INVALID_LAUNCH_PARAMS:
                        return "The experience could not be launched with the provided parameters. This typically means the product integration is missing or incomplete.";
                    case ConfigurationError.OVERRIDE_THEME_FAILED:
                        return "The specified override theme could not be loaded. It may have been deleted or is no longer available.";
                    case ConfigurationError.ACTIVATION_THEME_FAILED:
                        return "The theme for this activation could not be loaded. The activation may not have a theme configured.";
                    case ConfigurationError.TRANSACTION_THEME_FAILED:
                        return "The theme for this transaction could not be loaded. The transaction may not have a theme configured.";
                    case ConfigurationError.BUNDLE_THEME_FAILED:
                        return "The theme for this bundle could not be loaded. The bundle may not have a theme configured.";
                    case ConfigurationError.INTEGRATION_FETCH_FAILED:
                        return "Failed to load the integration configuration. The application key may be invalid or the integration is not properly set up.";
                    case ConfigurationError.NETWORK_ERROR:
                        return "A network error occurred while loading the theme. Please check your connection and try again.";
                    case ConfigurationError.SINGLE_PRODUCT_MISSING_PRODUCT:
                        return "This activation requires a product. Please launch from a product page.";
                    case ConfigurationError.SINGLE_PRODUCT_NOT_IN_COLLECTION:
                        return "This product is not configured for this activation.";
                    default:
                        return "An unexpected error occurred while loading the theme configuration.";
                }
            };

            const getDebugInfo = () => {
                const isDevelopment =
                    window.location.hostname === "localhost" || window.location.hostname.includes("dev");

                if (!isDevelopment || !errorDetails) return null;

                return (
                    <>
                        <div style={{ marginBottom: "12px" }}>
                            <strong>Error Code:</strong> {errorDetails.error}
                        </div>
                        {errorDetails.message && (
                            <div style={{ marginBottom: "12px" }}>
                                <strong>Message:</strong> {errorDetails.message}
                            </div>
                        )}
                        {errorDetails.context && Object.keys(errorDetails.context).length > 0 && (
                            <div>
                                <strong>Context:</strong>
                                <pre
                                    style={{
                                        marginTop: "8px",
                                        padding: "8px",
                                        backgroundColor: "#fff",
                                        borderRadius: "4px",
                                        fontSize: "0.7rem",
                                        overflow: "auto",
                                    }}
                                >
                                    {JSON.stringify(errorDetails.context, null, 2)}
                                </pre>
                            </div>
                        )}
                    </>
                );
            };

            return <ErrorPage title={getTitle()} description={getDescription()} debugInfo={getDebugInfo()} />;
        }

        if (loading || !options || !configuration) {
            return <FullScreenSpinner localizedMessageKey={"spinner.fetching"} noFade={true} />;
        }

        return (
            <MarketplaceThemeProvider
                applicationKey={applicationKey || ""}
                configuration={configuration}
                embedMode={false}
                presentmentCurrency={presentmentCurrency}
                options={options}
            />
        );
    };
};
